// Step 1 - which sequencer this run connects to.
//
// The run row carries the choice and everything downstream branches on it: the
// API key model, the paste's column count, the params shape, the connect flow.
// Which is exactly why CHANGING it mid-wizard resets steps 2-5 server-side -
// a PlusVibe key or a 4-column paste is meaningless to Instantly and vice
// versa. The server reports whether anything was actually lost (`cleared`) and
// this step warns before the click and toasts after it.

import { useState } from 'react';

import { sqApi, sqErrorMessage, type SqSequencer } from '../sequencerApi';
import type { SqStepProps } from './shared';

const OPTIONS: { value: SqSequencer; label: string; blurb: string }[] = [
  {
    value: 'plusvibe',
    label: 'PlusVibe',
    blurb:
      'Connects each mailbox by OAuth, then pushes our standard sending and warmup profile and the run tag.',
  },
  {
    value: 'instantly',
    label: 'Instantly',
    blurb:
      'Connects each mailbox by OAuth with one workspace API key, pushes our standard sending and warmup profile, then enables warmup, tags and the tracking domain in bulk.',
  },
  {
    value: 'smartlead',
    label: 'Smartlead',
    blurb:
      'Connects each mailbox by OAuth (Smartlead builds the consent URL from your workspace user id), then pushes our standard sending and warmup profile and the run tag.',
  },
  {
    value: 'bison',
    label: 'Bison',
    blurb:
      'White-label Email Bison instance. Needs the instance URL together with the API key; connects by OAuth session, then pushes daily limit, warmup and the run tag.',
  },
  {
    value: 'warmuptool',
    label: 'Warmuptool',
    blurb:
      'Your own Warmuptool instance. Needs the API URL together with the API key; connects each mailbox by OAuth session and stops there. Warmup and limits stay at the instance defaults.',
  },
];

export function StepSequencer({ runId, detail, reload, goToStep, toast }: SqStepProps): JSX.Element {
  const { run } = detail;

  const [choice, setChoice] = useState<SqSequencer>(run.sequencer);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The server resets steps 2-5 on a platform CHANGE; warn before the click
  // when that change would actually destroy something the operator entered.
  const willClear =
    choice !== run.sequencer && (run.has_api_key || run.accounts_total > 0 || run.params !== null);

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const { cleared } = await sqApi.setSequencer(runId, choice);
      if (cleared) {
        toast(
          'warn',
          'Switching sequencer cleared the API key, accounts, workspace and parameters - the later steps start fresh.',
        );
      }
      await reload();
      // The route advances the pointer to 2 itself; goToStep is still called so
      // the wizard does not depend on that side effect staying true.
      await goToStep(2);
    } catch (err) {
      setError(sqErrorMessage(err));
      toast('error', sqErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sq-step">
      <div className="sq-step-head">
        <h2>Step 1 - Sequencer</h2>
        <p>
          Which sending platform these mailboxes are being connected to. It decides how every later
          step talks to the customer's account.
        </p>
      </div>
      <div className="sq-step-body">
        {error && <div className="sq-notice sq-notice--err">{error}</div>}

        <div className="sq-radios">
          {OPTIONS.map((o) => (
            <label
              key={o.value}
              className={`sq-radio${choice === o.value ? ' sq-radio--on' : ''}`}
            >
              <input
                type="radio"
                name="sq-sequencer"
                value={o.value}
                checked={choice === o.value}
                onChange={() => setChoice(o.value)}
                disabled={busy}
              />
              <span className="sq-radio-text">
                <span className="sq-radio-label">{o.label}</span>
                <span className="sq-radio-blurb">{o.blurb}</span>
              </span>
            </label>
          ))}
        </div>

        {willClear && (
          <div className="sq-notice sq-notice--warn">
            Switching platform resets steps 2-5: the API key, the pasted accounts, the workspace
            and the parameters are all platform-specific and will be cleared.
          </div>
        )}

        <div className="sq-actions">
          <button
            type="button"
            className="sq-btn sq-btn--primary"
            onClick={() => void save()}
            disabled={busy}
          >
            {busy ? 'Saving...' : 'Next'}
          </button>
        </div>
      </div>
    </section>
  );
}
